import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, EyeOff, Film, Users, BarChart2, Search, Check, X, Pencil } from 'lucide-react';
import Header from '../../components/ui/Header';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { fetchWarmUps, deleteWarmUp, updateWarmUp } from '../../services/warmUpService';
import WarmUpFormModal from './components/WarmUpFormModal';

const DIFFICULTY_STYLES = {
  Beginner: 'bg-green-100 text-green-700',
  Intermediate: 'bg-yellow-100 text-yellow-700',
  Advanced: 'bg-red-100 text-red-700',
};

const WarmUpsManagement = () => {
  const { isSuperUser } = useAuth();
  const navigate = useNavigate();

  const [warmUps, setWarmUps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingWarmUp, setEditingWarmUp] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [editingTitleId, setEditingTitleId] = useState(null);
  const [editingTitleValue, setEditingTitleValue] = useState('');
  const [savingTitleId, setSavingTitleId] = useState(null);
  const titleInputRef = useRef(null);

  useEffect(() => {
    if (!isSuperUser()) {
      setError('Access denied. SuperUser privileges required.');
      setLoading(false);
      return;
    }
    loadWarmUps();
  }, []);

  useEffect(() => {
    if (editingTitleId && titleInputRef?.current) {
      titleInputRef?.current?.focus();
      titleInputRef?.current?.select();
    }
  }, [editingTitleId]);

  const loadWarmUps = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchWarmUps({ adminMode: true });
      setWarmUps(data);
    } catch (err) {
      setError(err?.message || 'Failed to load warm ups.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (saved) => {
    setShowFormModal(false);
    setEditingWarmUp(null);
    await loadWarmUps();
  };

  const handleEdit = (wu) => {
    setEditingWarmUp(wu);
    setShowFormModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this warm up? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteWarmUp(id);
      setWarmUps((prev) => prev?.filter((w) => w?.id !== id));
    } catch (err) {
      alert('Failed to delete: ' + err?.message);
    } finally {
      setDeletingId(null);
    }
  };

  const handleTogglePublished = async (wu) => {
    setTogglingId(wu?.id);
    try {
      await updateWarmUp(wu?.id, { published: !wu?.published });
      setWarmUps((prev) => prev?.map((w) => w?.id === wu?.id ? { ...w, published: !w?.published } : w));
    } catch (err) {
      alert('Failed to update: ' + err?.message);
    } finally {
      setTogglingId(null);
    }
  };

  const handleStartTitleEdit = (wu) => {
    setEditingTitleId(wu?.id);
    setEditingTitleValue(wu?.title || '');
  };

  const handleSaveTitleEdit = async (id) => {
    const trimmed = editingTitleValue?.trim();
    if (!trimmed) return;
    setSavingTitleId(id);
    try {
      await updateWarmUp(id, { title: trimmed });
      setWarmUps((prev) => prev?.map((w) => w?.id === id ? { ...w, title: trimmed } : w));
      setEditingTitleId(null);
      setEditingTitleValue('');
    } catch (err) {
      alert('Failed to update title: ' + err?.message);
    } finally {
      setSavingTitleId(false);
    }
  };

  const handleCancelTitleEdit = () => {
    setEditingTitleId(null);
    setEditingTitleValue('');
  };

  const handleTitleKeyDown = (e, id) => {
    if (e?.key === 'Enter') handleSaveTitleEdit(id);
    if (e?.key === 'Escape') handleCancelTitleEdit();
  };

  const getTalentName = (wu) => {
    const t = wu?.talent;
    if (!t) return '—';
    return t?.display_name || `${t?.first_name || ''} ${t?.last_name || ''}`?.trim() || '—';
  };

  const filtered = warmUps?.filter((wu) =>
    !searchQuery || wu?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) || getTalentName(wu)?.toLowerCase()?.includes(searchQuery?.toLowerCase())
  );

  if (loading) return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex items-center justify-center h-64"><LoadingSpinner /></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <Helmet><title>Warm Ups Management — RoleCall</title></Helmet>
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Warm Ups</h1>
            <p className="text-muted-foreground text-sm mt-1">Manage daily vocal warm up videos for students</p>
          </div>
          <button
            onClick={() => { setEditingWarmUp(null); setShowFormModal(true); }}
            className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl font-medium hover:opacity-90 transition-opacity text-sm"
          >
            <Plus className="w-4 h-4" />
            New Warm Up
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 text-red-700 text-sm">{error}</div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                <Film className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{warmUps?.length}</p>
                <p className="text-xs text-muted-foreground">Total Warm Ups</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{warmUps?.filter((w) => w?.published)?.length}</p>
                <p className="text-xs text-muted-foreground">Published</p>
              </div>
            </div>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <BarChart2 className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-foreground">{warmUps?.reduce((sum, w) => sum + (w?.view_count || 0), 0)}</p>
                <p className="text-xs text-muted-foreground">Total Views</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            placeholder="Search warm ups..."
            className="w-full pl-9 pr-4 py-2.5 border border-border rounded-xl text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {/* Table */}
        {filtered?.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <Film className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-40" />
            <p className="text-foreground font-medium">{searchQuery ? 'No warm ups match your search' : 'No warm ups yet'}</p>
            <p className="text-muted-foreground text-sm mt-1">{!searchQuery && 'Create your first warm up to get started.'}</p>
            {!searchQuery && (
              <button
                onClick={() => { setEditingWarmUp(null); setShowFormModal(true); }}
                className="mt-4 flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-xl font-medium hover:opacity-90 transition-opacity text-sm mx-auto"
              >
                <Plus className="w-4 h-4" />
                New Warm Up
              </button>
            )}
          </div>
        ) : (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Title</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Talent</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Difficulty</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Views</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Video</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered?.map((wu) => (
                  <tr key={wu?.id} className="hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      {editingTitleId === wu?.id ? (
                        <div className="flex items-center gap-1.5">
                          <input
                            ref={titleInputRef}
                            type="text"
                            value={editingTitleValue}
                            onChange={(e) => setEditingTitleValue(e?.target?.value)}
                            onKeyDown={(e) => handleTitleKeyDown(e, wu?.id)}
                            disabled={savingTitleId === wu?.id}
                            className="flex-1 min-w-0 text-sm font-medium text-foreground border border-primary/50 rounded-lg px-2 py-1 bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                          />
                          <button
                            onClick={() => handleSaveTitleEdit(wu?.id)}
                            disabled={savingTitleId === wu?.id || !editingTitleValue?.trim()}
                            className="p-1 text-green-600 hover:bg-green-50 rounded-md transition-colors disabled:opacity-40"
                            title="Save title"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelTitleEdit}
                            disabled={savingTitleId === wu?.id}
                            className="p-1 text-muted-foreground hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                            title="Cancel"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-start gap-1.5 group">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground">{wu?.title}</p>
                            {wu?.description && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{wu?.description}</p>}
                          </div>
                          <button
                            onClick={() => handleStartTitleEdit(wu)}
                            className="p-1 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0 mt-0.5"
                            title="Edit title"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {wu?.talent?.headshot_url ? (
                          <img src={wu?.talent?.headshot_url} alt={getTalentName(wu)} className="w-6 h-6 rounded-full object-cover" />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
                            <Users className="w-3 h-3 text-muted-foreground" />
                          </div>
                        )}
                        <span className="text-sm text-foreground">{getTalentName(wu)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {wu?.difficulty_level ? (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${DIFFICULTY_STYLES?.[wu?.difficulty_level] || 'bg-gray-100 text-gray-600'}`}>
                          {wu?.difficulty_level}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-medium text-foreground">{wu?.view_count || 0}</span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {wu?.mux_playback_id ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <Film className="w-3 h-3" /> Ready
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          No video
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleTogglePublished(wu)}
                        disabled={togglingId === wu?.id}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                          wu?.published ? 'bg-green-100 text-green-700 hover:bg-green-200' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {wu?.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {wu?.published ? 'Published' : 'Draft'}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/warm-ups/${wu?.id}`)}
                          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(wu)}
                          className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(wu?.id)}
                          disabled={deletingId === wu?.id}
                          className="p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {showFormModal && (
        <WarmUpFormModal
          warmUp={editingWarmUp}
          onSave={handleSave}
          onClose={() => { setShowFormModal(false); setEditingWarmUp(null); }}
        />
      )}
    </div>
  );
};

export default WarmUpsManagement;
